/**
 * Blume-Ideal Deal-Seite – gemeinsames Verhalten für alle bd-* Sektionen.
 *
 * Enthält:
 *   1. <bd-countdown>  – Deal-Countdown mit Doppelpunkten (Enddatum via Attribut)
 *   2. Reveal-on-Scroll – sanftes Einblenden von .bd-reveal Elementen
 *
 * Beide Teile sind idempotent: Die Datei kann von mehreren Sektionen geladen
 * werden, ohne doppelt zu initialisieren.
 */

/* ------------------------------------------------------------------ *
 * 1. Countdown
 * ------------------------------------------------------------------ */
if (!customElements.get('bd-countdown')) {
  class BdCountdown extends HTMLElement {
    connectedCallback() {
            const raw = (this.getAttribute('end') || '').trim();
      // "midnight"/"mitternacht" => rollierender Countdown bis 0 Uhr, startet danach neu
      this.midnight = /^(midnight|mitternacht)$/i.test(raw);
      this.end = this.midnight ? this.nextMidnight() : raw ? new Date(raw.replace(' ', 'T')).getTime() : NaN;

      this.boxes = {
        d: this.querySelector('[data-unit="d"]'),
        h: this.querySelector('[data-unit="h"]'),
        m: this.querySelector('[data-unit="m"]'),
        s: this.querySelector('[data-unit="s"]'),
      };
      this.sepDay = this.querySelector('[data-sep="d"]');
      this.sepSec = this.querySelector('[data-sep="s"]');

      if (isNaN(this.end)) return; // ungültiges Datum -> statisches Markup behalten

      this.update();
      this.timer = setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

        nextMidnight() {
      const d = new Date();
      d.setHours(24, 0, 0, 0);
      return d.getTime();
    }

    update() {
      let diff = this.end - Date.now();
      if (diff <= 0 && this.midnight) {
        this.end = this.nextMidnight();
        diff = this.end - Date.now();
      }
      if (diff <= 0) {
        diff = 0;
        clearInterval(this.timer);
        this.setAttribute('expired', '');
      }
      const total = Math.floor(diff / 1000);
      const days = Math.floor(total / 86400);
      const hours = Math.floor((total % 86400) / 3600);
      const mins = Math.floor((total % 3600) / 60);
      const secs = total % 60;

      // Immer drei Boxen: >=1 Tag => T:Std:Min, sonst Std:Min:Sek
      const showDays = days > 0;
      this.toggle(this.boxes.d, showDays);
      this.toggle(this.sepDay, showDays);
      this.toggle(this.boxes.s, !showDays);
      this.toggle(this.sepSec, !showDays);

      this.set(this.boxes.d, days);
      this.set(this.boxes.h, hours);
      this.set(this.boxes.m, mins);
      this.set(this.boxes.s, secs);
    }

    set(el, val) {
      if (el) el.textContent = String(val).padStart(2, '0');
    }
    toggle(el, show) {
      if (el) el.hidden = !show;
    }
  }
  customElements.define('bd-countdown', BdCountdown);
}

/* ------------------------------------------------------------------ *
 * 2. Reveal-on-Scroll (sanft, gestaffelt, respektiert reduced-motion)
 * ------------------------------------------------------------------ */
if (!window.__bdRevealInit) {
  window.__bdRevealInit = true;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const revealAll = () => {
    const items = document.querySelectorAll('.bd-reveal:not(.bd-reveal--in)');
    if (reduce || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('bd-reveal--in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('bd-reveal--in');
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );
    items.forEach((el) => io.observe(el));
  };

  if (document.readyState !== 'loading') {
    revealAll();
  } else {
    document.addEventListener('DOMContentLoaded', revealAll);
  }
  // Theme-Editor: neu geladene Sektionen ebenfalls berücksichtigen
  document.addEventListener('shopify:section:load', revealAll);
}

/* ------------------------------------------------------------------ *
 * 3. Journey – gepinnter Step-Slider mit Scroll-Hijack
 *    Genau EIN Schritt pro Scroll-/Swipe-Geste (700ms Sperre),
 *    weicher Fade+Slide. Desktop und Mobil identisch.
 *    reduced-motion / kein JS  ->  einfache, lesbare Liste.
 * ------------------------------------------------------------------ */
if (!window.__bdJourneyInit) {
  window.__bdJourneyInit = true;

  const initJourney = () => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return; // Fallback-Liste

    document.querySelectorAll('.bd-journey').forEach((sec) => {
      if (sec.__bdJourney) return;
      const imgs = [...sec.querySelectorAll('.bd-journey__img')];
      const texts = [...sec.querySelectorAll('.bd-journey__text')];
      const dots = [...sec.querySelectorAll('.bd-journey__dot')];
      const textsWrap = sec.querySelector('.bd-journey__texts');
      const n = texts.length;
      if (!textsWrap || n === 0) return;
      sec.__bdJourney = true;

      // Höhe der Scroll-Strecke: 100vh (gepinnt) + (n-1) * Schrittlänge
      const stepVh = parseFloat(sec.dataset.stepVh || '80');
      const applyHeight = () => {
        sec.style.setProperty('--bd-step-h', stepVh + 'vh');
        sec.style.height = 100 + (n - 1) * stepVh + 'vh';
      };
      applyHeight();

      // Text-Box auf die Höhe des längsten Schritts fixieren (Messung im Fluss,
      // damit es auch nach Font-Load / Resize stimmt, obwohl die Texte gestapelt sind).
      const sizeTexts = () => {
        textsWrap.style.minHeight = '';
        texts.forEach((t) => { t.style.position = 'static'; });
        let max = 0;
        texts.forEach((t) => { max = Math.max(max, t.offsetHeight); });
        texts.forEach((t) => { t.style.position = ''; });
        textsWrap.style.minHeight = max + 'px';
      };
      sizeTexts();
      sec.classList.add('bd-journey--ready');
      if (document.fonts && document.fonts.ready) { document.fonts.ready.then(sizeTexts); }
      window.addEventListener('load', sizeTexts, { once: true });

      let active = -1;
      const setActive = (i) => {
        i = Math.max(0, Math.min(n - 1, i));
        if (i === active) return;
        active = i;
        imgs.forEach((e, k) => e.classList.toggle('is-active', k === i));
        texts.forEach((e, k) => e.classList.toggle('is-active', k === i));
        dots.forEach((e, k) => e.classList.toggle('is-active', k === i));
      };
      setActive(0);

      // Aktiver Schritt aus der Scroll-Position innerhalb der Strecke
      let ticking = false;
      const update = () => {
        ticking = false;
        const total = sec.offsetHeight - window.innerHeight; // Länge der gepinnten Strecke
        if (total <= 0) { setActive(0); return; }
        const passed = Math.min(Math.max(-sec.getBoundingClientRect().top, 0), total);
        const idx = Math.round((passed / total) * (n - 1));
        setActive(idx);
      };
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };
      update();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', () => { applyHeight(); sizeTexts(); update(); }, { passive: true });

      // Sanftes Einrasten pro Schritt nur, während die Sektion im Blick ist
      const snapIO = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          document.documentElement.style.scrollSnapType = entry.isIntersecting ? 'y proximity' : '';
        });
      }, { threshold: 0 });
      snapIO.observe(sec);
    });
  };

  if (document.readyState !== 'loading') {
    initJourney();
  } else {
    document.addEventListener('DOMContentLoaded', initJourney);
  }
  document.addEventListener('shopify:section:load', initJourney);
}

/* ------------------------------------------------------------------ *
 * 4. Reviews-Slider – Prev/Next-Pfeile scrollen den Track
 * ------------------------------------------------------------------ */
if (!window.__bdReviewsInit) {
  window.__bdReviewsInit = true;

  const initReviews = () => {
    document.querySelectorAll('.bd-reviews').forEach((sec) => {
      if (sec.__bdReviews) return;
      const track = sec.querySelector('.bd-reviews__track');
      const arrows = sec.querySelectorAll('.bd-reviews__arrow');
      if (!track || arrows.length === 0) return;
      sec.__bdReviews = true;

      const step = () => {
        const card = track.querySelector('.bd-review');
        if (!card) return track.clientWidth * 0.8;
        const gap = parseFloat(getComputedStyle(track).columnGap) || 20;
        return card.getBoundingClientRect().width + gap;
      };

      arrows.forEach((btn) => {
        btn.addEventListener('click', () => {
          const dir = btn.dataset.dir === 'prev' ? -1 : 1;
          track.scrollBy({ left: dir * step(), behavior: 'smooth' });
        });
      });
    });
  };

  if (document.readyState !== 'loading') {
    initReviews();
  } else {
    document.addEventListener('DOMContentLoaded', initReviews);
  }
  document.addEventListener('shopify:section:load', initReviews);
}
